import { z } from 'zod';

import { apiRequest, apiStream, type StreamOptions } from '@/api/client';

/**
 * One-click storefront setup: a named bundle of changes (theme, homepage,
 * starter products, banners, copy) that is previewed, run with live progress,
 * and can be undone as a whole.
 */

export const SETUP_ACTION_KEYS = [
  'FULL_SETUP',
  'DEFAULT_SETUP',
  'THEME_AND_HOMEPAGE',
  'PRODUCTS_AND_CATEGORIES',
  'BANNERS_AND_IMAGES',
  'PERSONALIZE',
  'OPTIMIZE',
] as const;

export type SetupActionKey = (typeof SETUP_ACTION_KEYS)[number];

export function isSetupActionKey(value: string): value is SetupActionKey {
  return (SETUP_ACTION_KEYS as readonly string[]).includes(value);
}

export const setupActionSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  uses_ai: z.boolean(),
  cost_generations: z.number(),
  available: z.boolean(),
  unavailable_reason: z.string().nullable(),
});

export type SetupAction = z.infer<typeof setupActionSchema>;

/**
 * The store a setup will create first, for a merchant who has none yet.
 *
 * Present only then. The window shows it as the "Your store" card: the name
 * and web address the server suggests (with whether that address is free, and
 * others if it is not), and the theme suggested for the merchant's industry -
 * `suggested_theme` is a theme id this build ships (`serra`), and
 * `suggested_preset` the server's preset key for it.
 */
export const setupStoreSchema = z.object({
  name: z.string(),
  subdomain: z.string(),
  subdomain_available: z.boolean(),
  subdomain_suggestions: z.array(z.string()).default([]),
  suggested_theme: z.string(),
  suggested_preset: z.string().nullish().transform((value) => value ?? null),
});

export type SetupStore = z.infer<typeof setupStoreSchema>;

export const setupPreviewSchema = z.object({
  action: z.string(),
  steps: z.array(z.object({ key: z.string(), label: z.string(), detail: z.string().nullable().optional() })),
  changes: z.array(z.string()),
  cost_generations: z.number(),
  free_first_run: z.boolean(),
  blockers: z.array(z.object({ code: z.string(), message: z.string(), to: z.string().nullable() })),
  warnings: z.array(z.string()),
  // Absent from a server that predates it, which is the same as a store
  // already existing.
  store_setup: setupStoreSchema.nullish().transform((value) => value ?? null),
});

export type SetupPreview = z.infer<typeof setupPreviewSchema>;

export const SETUP_STEP_STATUSES = ['running', 'done', 'skipped', 'failed'] as const;
export type SetupStepStatus = (typeof SETUP_STEP_STATUSES)[number];

export const setupStepEventSchema = z.object({
  key: z.string(),
  label: z.string(),
  status: z.enum(SETUP_STEP_STATUSES),
  detail: z.string().nullable().optional(),
});

export type SetupStepEvent = z.infer<typeof setupStepEventSchema>;

export const setupRunSchema = z.object({
  id: z.string(),
  action: z.string(),
  status: z.enum(['RUNNING', 'COMPLETED', 'FAILED', 'UNDONE']),
  created_at: z.string(),
  charged_generations: z.number(),
  undo_available: z.boolean(),
  // Counts of what was applied. Not drawn (the `lines` say it in words), so
  // read loosely: a new counter must not fail a run that has already happened.
  summary: z.record(z.string(), z.unknown()),
  lines: z.array(z.string()),
});

export type SetupRun = z.infer<typeof setupRunSchema>;

export function fetchSetupActions() {
  return apiRequest('/assistant/setup/actions', z.array(setupActionSchema));
}

export function previewSetup(action: SetupActionKey) {
  return apiRequest('/assistant/setup/preview', setupPreviewSchema, { method: 'POST', body: { action } });
}

/** Run a setup; `step` events report each step as it starts and finishes. */
export function runSetup(action: SetupActionKey, options: Pick<StreamOptions, 'signal' | 'onEvent'> = {}) {
  return apiStream('/assistant/setup/run', z.object({ run: setupRunSchema }), {
    method: 'POST',
    body: { action },
    ...options,
  });
}

export const SUBDOMAIN_REFUSALS = ['INVALID', 'RESERVED', 'TAKEN'] as const;
export type SubdomainRefusal = (typeof SUBDOMAIN_REFUSALS)[number];

export const setupSubdomainCheckSchema = z.object({
  value: z.string(),
  available: z.boolean(),
  // A reason this build does not know is still a refusal, just an unnamed one.
  reason: z.enum(SUBDOMAIN_REFUSALS).nullable().catch('INVALID'),
  suggestions: z.array(z.string()).default([]),
});

export type SetupSubdomainCheck = z.infer<typeof setupSubdomainCheckSchema>;

/** Whether a web address is free for a store that does not exist yet. */
export function checkSetupSubdomain(value: string) {
  return apiRequest(
    `/assistant/setup/subdomain-check?value=${encodeURIComponent(value)}`,
    setupSubdomainCheckSchema,
  );
}

export function fetchLatestSetupRun() {
  return apiRequest('/assistant/setup/runs/latest', setupRunSchema.nullable());
}

export function undoSetupRun(id: string) {
  return apiRequest(`/assistant/setup/runs/${id}/undo`, setupRunSchema, { method: 'POST' });
}

export const setupKeys = {
  actions: ['assistant', 'setup', 'actions'] as const,
  preview: (action: SetupActionKey) => ['assistant', 'setup', 'preview', action] as const,
  latestRun: ['assistant', 'setup', 'latest-run'] as const,
  subdomainCheck: (value: string) => ['assistant', 'setup', 'subdomain-check', value] as const,
};
